import {
  BookOpenIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  CalendarDaysIcon,
  ShoppingBagIcon,
  TrophyIcon,
  AcademicCapIcon,
  UserGroupIcon,
  UsersIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { AppStripItemConfig } from "./appStripTypes";

/**
 * App Strip configuration
 *
 * Each item can have:
 * - permissions: Direct permission check (user needs ANY one)
 * - navItemInternalNames: Shows if ANY of these nav items are visible
 * - isCertainPathOnly: Only for CertainPath companies
 *
 * For sections (Resources, Stochastic, Admin), visibility is derived from
 * whether the user can see any nav items in that section.
 *
 * For external links (Scoreboard, Training, Partner Network), we use direct
 * permissions since they don't have child nav items.
 */
export const appStripItems: AppStripItemConfig[] = [
  {
    id: "resources",
    name: "Resources",
    icon: BookOpenIcon,
    defaultRoute: "/hub",
  },
  {
    id: "stochastic",
    name: "Lead Generation",
    icon: Squares2X2Icon,
    defaultRoute: "/lead-generation/sales-and-customer-dashboard",
    // Visibility derived from stochastic nav items
    navItemInternalNames: [
      "dashboard",
      "roi_dashboard",
      "prospects",
      "customers",
      "invoices",
      "locations",
      "campaigns",
      "dashboards", // uploads parent
    ],
  },
  {
    id: "dashboards",
    name: "Data Connector",
    icon: ChartBarSquareIcon,
    defaultRoute: "/hub/data-connector",
    // Visibility based on data-connector permission
    navItemInternalNames: ["data_connector"],
    standalone: true,
  },
  {
    id: "events",
    name: "Events",
    icon: CalendarDaysIcon,
    defaultRoute: "/event-registration/events",
    // Visibility derived from event-related nav items
    navItemInternalNames: ["event-directory"],
  },
  {
    id: "shop",
    name: "Shop",
    icon: ShoppingBagIcon,
    defaultRoute: "/subscriptions/total-access",
    navItemInternalNames: ["total-access", "product-directory-browse"],
  },
  {
    id: "scoreboard",
    name: "Scoreboard",
    icon: TrophyIcon,
    isExternal: true,
    externalUrl: import.meta.env.VITE_SCOREBOARD_URL || "",
    // Direct permission for external link — any scoreboard permission grants access
    permissions: ["scoreboard", "scoreboard-admin", "scoreboard-coach"],
  },
  {
    id: "training",
    name: "Training",
    icon: AcademicCapIcon,
    isExternal: true,
    externalUrl: import.meta.env.VITE_MEMBER_PORTAL_URL || "",
    // Direct permission for external link — any training-portal permission grants access
    permissions: ["training-portal", "training-portal-admin"],
  },
  {
    id: "employee-directory",
    name: "Employee Directory",
    icon: UserGroupIcon,
    defaultRoute: "/employee-directory",
    standalone: true,
    // Live for all authenticated company users. (Was temporarily
    // CertainPath-only until the directory was ready to go live.)
  },
  {
    id: "partner-network",
    name: "Partner Network",
    icon: UsersIcon,
    isExternal: true,
    externalUrl: import.meta.env.VITE_PARTNER_NETWORK_URL || "",
    // Direct permission for external link — any partner-network permission grants access
    permissions: ["partner-network", "partner-network-admin"],
  },
  {
    id: "admin",
    name: "Admin",
    icon: Cog6ToothIcon,
    defaultRoute: "/admin/events",
    // Visibility derived from admin nav items (both internal admin and company admin)
    navItemInternalNames: [
      // Internal Admin items (CertainPath employees)
      "event-registration-admin",
      "email_management",
      "resource_management",
      "user_management",
      "admin", // stochastic admin section
      // Company Admin items (company admins)
      "company_user_management",
      "company_settings",
    ],
  },
];
